import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { BadgeShape, BadgeTier, TIER_COLORS } from 'types/interfaces/badge';
import { getIconForSlug } from './badgeIconMap';
import './BadgeIcon.css';

interface BadgeIconProps {
  icon: string;
  shape: BadgeShape;
  tier: BadgeTier;
  size?: number;
  earned?: boolean;
  className?: string;
  onClick?: () => void;
}

const MYTHIC_GRADIENT_ID = 'badge-mythic-gradient';

const ShieldShape: React.FC<{ color: string; size: number; isMythic: boolean }> = ({ color, size, isMythic }) => {
  const stroke = isMythic ? `url(#${MYTHIC_GRADIENT_ID})` : color;
  // Shield path scaled to viewBox 0 0 100 120
  return (
    <>
      <path
        d="M50 4 L92 20 L92 65 Q92 90 50 116 Q8 90 8 65 L8 20 Z"
        fill="#1a1025"
        stroke={stroke}
        strokeWidth="3"
      />
      <path
        d="M50 10 L86 24 L86 63 Q86 85 50 109 Q14 85 14 63 L14 24 Z"
        fill="none"
        stroke={stroke}
        strokeWidth="0.5"
        opacity="0.25"
      />
    </>
  );
};

const CircleShape: React.FC<{ color: string; size: number; isMythic: boolean }> = ({ color, size, isMythic }) => {
  const stroke = isMythic ? `url(#${MYTHIC_GRADIENT_ID})` : color;
  const cx = 50;
  const cy = 55;
  const r = 42;
  // Decorative notch lines at 8 compass points
  const notches = [0, 45, 90, 135, 180, 225, 270, 315].map((angle) => {
    const rad = (angle * Math.PI) / 180;
    const x1 = cx + (r - 4) * Math.cos(rad);
    const y1 = cy + (r - 4) * Math.sin(rad);
    const x2 = cx + (r + 4) * Math.cos(rad);
    const y2 = cy + (r + 4) * Math.sin(rad);
    return <line key={angle} x1={x1} y1={y1} x2={x2} y2={y2} stroke={stroke} strokeWidth="2" />;
  });

  return (
    <>
      <circle cx={cx} cy={cy} r={r} fill="#1a1025" stroke={stroke} strokeWidth="3" />
      <circle cx={cx} cy={cy} r={r - 6} fill="none" stroke={stroke} strokeWidth="0.5" opacity="0.25" />
      {notches}
    </>
  );
};

const HexShape: React.FC<{ color: string; size: number; isMythic: boolean }> = ({ color, size, isMythic }) => {
  const stroke = isMythic ? `url(#${MYTHIC_GRADIENT_ID})` : color;
  const cx = 50;
  const cy = 55;
  const r = 44;
  const ri = r - 7;

  const hexPoints = (radius: number) =>
    [0, 1, 2, 3, 4, 5]
      .map((i) => {
        const angle = (Math.PI / 3) * i - Math.PI / 2;
        return `${cx + radius * Math.cos(angle)},${cy + radius * Math.sin(angle)}`;
      })
      .join(' ');

  return (
    <>
      <polygon points={hexPoints(r)} fill="#1a1025" stroke={stroke} strokeWidth="3" />
      <polygon points={hexPoints(ri)} fill="none" stroke={stroke} strokeWidth="0.5" opacity="0.25" />
    </>
  );
};

const BadgeIcon: React.FC<BadgeIconProps> = ({
  icon,
  shape,
  tier,
  size = 48,
  earned = true,
  className = '',
  onClick,
}) => {
  const isMythic = tier === 'mythic';
  const color = TIER_COLORS[tier];
  const faIcon = getIconForSlug(icon);
  const viewBoxHeight = shape === 'shield' ? 120 : 110;

  const shapeProps = { color, size, isMythic };

  return (
    <svg
      className={`badge-icon badge-icon--${shape} badge-icon--${tier} ${earned ? 'badge-icon--earned' : 'badge-icon--locked'} ${className}`}
      width={size}
      height={size * (viewBoxHeight / 100)}
      viewBox={`0 0 100 ${viewBoxHeight}`}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      style={{ cursor: onClick ? 'pointer' : undefined }}
    >
      {isMythic && (
        <defs>
          <linearGradient id={MYTHIC_GRADIENT_ID} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#EE5000" />
            <stop offset="50%" stopColor="#F69000" />
            <stop offset="100%" stopColor="#EE5000" />
          </linearGradient>
        </defs>
      )}

      {shape === 'shield' && <ShieldShape {...shapeProps} />}
      {shape === 'circle' && <CircleShape {...shapeProps} />}
      {shape === 'hex' && <HexShape {...shapeProps} />}

      {/* Inner icon via foreignObject */}
      <foreignObject
        x="20"
        y={shape === 'shield' ? '30' : '25'}
        width="60"
        height="60"
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100%',
          }}
        >
          <FontAwesomeIcon
            icon={faIcon}
            style={{
              color: isMythic ? '#F69000' : color,
              fontSize: `${size * 0.45}px`,
            }}
          />
        </div>
      </foreignObject>
    </svg>
  );
};

export default BadgeIcon;
